import { useNavigate, useSearchParams } from 'react-router'

import { Params } from '@/@types/routes'
import { toQueryString } from '@/utils/to-query-string'

type Options = { replace?: boolean }

export function useQueryParams() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const getQueryParams = (): Params => {
    const params: Params = {}

    for (const [key, value] of searchParams.entries()) {
      params[key] = value
    }

    return params
  }

  const applyQueryParams = (params: Params, options?: Options) => {
    const queryString = toQueryString(params)
    navigate({ search: queryString }, { replace: options?.replace })
  }

  const setQueryParams = (newParams: Params, options?: Options) => {
    applyQueryParams(newParams, options)
  }

  const mergeQueryParams = (newParams: Params, options?: Options) => {
    const merged = Object.fromEntries(searchParams.entries()) as Params

    for (const key in newParams) {
      const value = newParams[key]
      if (value !== undefined && value !== null && value !== '') {
        merged[key] = value
      } else {
        delete merged[key]
      }
    }

    applyQueryParams(merged, options)
  }

  const removeQueryParams = (keys: string | string[], options?: Options) => {
    const current = Object.fromEntries(searchParams.entries()) as Params
    const keysToRemove = Array.isArray(keys) ? keys : [keys]

    for (const key of keysToRemove) {
      delete current[key]
    }

    applyQueryParams(current, options)
  }

  return {
    getQueryParams,
    setQueryParams,
    mergeQueryParams,
    removeQueryParams,
  }
}
